import React, { useRef, useCallback } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { FieldVisitAssistDetailResp } from "domain/payload/response/assist/FieldVisitAssistDetailResp";
import Map, { Marker, MapRef } from 'react-map-gl/mapbox'

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN ?? "";

const VISIT_TYPE_BADGE: Record<string, string> = {
  witness: "primary",
  backcheck: "warning",
  other: "secondary",
};

const VISIT_TYPE_LABEL: Record<string, string> = {
  witness: "Witness",
  backcheck: "Backcheck",
  other: "Other",
};

interface Props {
  show: boolean;
  loading: boolean;
  data: FieldVisitAssistDetailResp | null;
  onClose: () => void;
}

const FieldVisitDetailModal: React.FC<Props> = ({ show, loading, data, onClose }) => {
  const mapRef = useRef<MapRef>(null);

  const fitBoundsToMarkers = useCallback(() => {
    if (!mapRef.current || !data) return;
    mapRef.current.flyTo({
      center: [data.longitude, data.latitude],
      zoom: 15,
      duration: 800,
    });
  }, [data]);

  if (!show) return null;

  return (
    <div
      className="modal fade show"
      style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          {/* ── Header ── */}
          <div className="modal-header border-bottom px-4 py-3">
            <div>
              <h5 className="fw-bold mb-0">Detail Field Visit</h5>
              {data && (
                <small className="text-muted" style={{ fontSize: 11 }}>
                  ID: {data.id}
                </small>
              )}
            </div>
          </div>

          {/* ── Body ── */}
          <div className="modal-body px-4 py-3">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted mt-2 mb-0">Memuat data...</p>
              </div>
            ) : data ? (
              <div className="row g-4">

                {/* ══ Kolom Kiri ══ */}
                <div className="col-lg-7">

                  {/* ── Informasi Kunjungan ── */}
                  <div className="mb-4">
                    <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                      Informasi Kunjungan
                    </p>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Project</label>
                        <p className="mb-0">{data.projectName || "-"}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Jenis Kunjungan</label>
                        <p className="mb-0">
                          <span className={`badge bg-${VISIT_TYPE_BADGE[data.visitType] ?? "secondary"}`}>
                            {VISIT_TYPE_LABEL[data.visitType] ?? data.visitType}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Dilakukan Oleh</label>
                        <p className="mb-0">{data.userName || "-"}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Subjek</label>
                        <p className="mb-0">{data.subjectName || "-"}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Alasan</label>
                        <p className="mb-0">{data.reason || "-"}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Tanggal Submit</label>
                        <p className="mb-0">{data.createdAt || "-"}</p>
                      </div>
                    </div>
                  </div>

                  <hr />

                  {/* ── Catatan ── */}
                  <div className="mb-4">
                    <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                      Catatan
                    </p>
                    <div className="row g-3">
                      <div className="col-12">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Catatan Hasil</label>
                        <p className="mb-0 text-pre-wrap">
                          {data.resultNotes?.trim() ? data.resultNotes : <span className="text-muted fst-italic">Tidak ada catatan hasil</span>}
                        </p>
                      </div>
                      <div className="col-12">
                        <label className="form-label fw-semibold mb-1" style={{ fontSize: 12 }}>Tindak Lanjut</label>
                        <p className="mb-0 text-pre-wrap">
                          {data.followUp?.trim() ? data.followUp : <span className="text-muted fst-italic">Tidak ada tindak lanjut</span>}
                        </p>
                      </div>
                    </div>
                  </div>

                  <hr />

                  {/* ── Titik Lokasi ── */}
                  <div>
                    <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                      Titik Lokasi
                    </p>
                    <div className="d-flex gap-3 mb-2">
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        <i className="fa-solid fa-location-dot me-1 text-danger"></i>
                        {data.latitude.toFixed(6)}, {data.longitude.toFixed(6)}
                      </span>
                    </div>
                    <div
                      className="rounded overflow-hidden border"
                      style={{ height: 280, width: "100%" }}
                    >
                      {data.latitude && data.longitude ? (
                        <Map
                          ref={mapRef}
                          mapboxAccessToken={MAPBOX_TOKEN}
                          initialViewState={{
                            longitude: data.longitude,
                            latitude: data.latitude,
                            zoom: 15,
                          }}
                          style={{ width: "100%", height: "100%" }}
                          mapStyle="mapbox://styles/mapbox/streets-v12"
                          onLoad={() => {
                            setTimeout(fitBoundsToMarkers, 300);
                          }}
                        >
                          <Marker
                            longitude={data.longitude}
                            latitude={data.latitude}
                            anchor="bottom"
                          >
                            <img
                                src="/assets/markers/icon-marker.svg"
                                style={{ width: 32, height: 32 }}
                                alt=""
                            />
                          </Marker>
                        </Map>
                      ) : (
                        <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                          <div className="text-center">
                            <i className="fa-solid fa-map fa-2x mb-2"></i>
                            <p className="mb-0">Lokasi tidak tersedia</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* ══ Kolom Kanan — Evidence ══ */}
                <div className="col-lg-5">
                  <p className="fw-semibold text-muted mb-2" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                    Evidence ({data.evidence?.length ?? 0})
                  </p>

                  {data.evidence && data.evidence.length > 0 ? (
                    <div
                      className="d-flex flex-column gap-3 pe-1"
                    >
                      {data.evidence.map((ev, idx) => (
                        <div key={idx} className="border rounded overflow-hidden">
                          {/* Label */}
                          <div className="px-3 py-2 bg-light border-bottom d-flex align-items-center gap-2">
                            <span
                              className="badge bg-secondary"
                              style={{ fontSize: 10 }}
                            >
                              {idx + 1}
                            </span>
                            <span className="fw-semibold" style={{ fontSize: 13 }}>
                              {ev.label || `Evidence ${idx + 1}`}
                            </span>
                          </div>

                          {/* Gambar */}
                          {ev.imageUrl ? (
                            <a href={ev.imageUrl} target="_blank" rel="noopener noreferrer">
                              <img
                                src={ev.imageUrl}
                                alt={ev.label}
                                style={{ display: "block", width: "100%", height: "auto" }}
                              />
                            </a>
                          ) : (
                            <div
                              className="d-flex align-items-center justify-content-center text-muted bg-light"
                              style={{ height: 120 }}
                            >
                              <div className="text-center">
                                <i className="fa-solid fa-image fa-2x mb-1"></i>
                                <p className="mb-0" style={{ fontSize: 12 }}>
                                  Gambar tidak tersedia
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center border rounded text-muted"
                      style={{ height: 160 }}
                    >
                      <div className="text-center">
                        <i className="fa-solid fa-images fa-2x mb-2"></i>
                        <p className="mb-0">Tidak ada evidence</p>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            ) : (
              <div className="text-center py-5">
                <i className="fa-solid fa-circle-exclamation fa-2x text-danger mb-2"></i>
                <p className="text-danger mb-0">Gagal memuat data detail</p>
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="modal-footer border-top px-4 py-3">
            <button className="btn btn-secondary" onClick={onClose}>
              Tutup
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldVisitDetailModal;